import React from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import logger from '../utils/logger';
import './ErrorBoundary.css';

/**
 * ErrorBoundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 * 
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    const errorDetails = {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    logger.error('ErrorBoundary caught an error:', errorDetails);

    // Update state with error details
    this.setState({
      error,
      errorInfo
    });

    // In production, send to error tracking service
    if (process.env.NODE_ENV === 'production') {
      // Example: Sentry.captureException(error, { extra: errorInfo });
      // For now, we'll just log it
      console.error('Production error:', errorDetails);
    }
  }

  handleReload = () => {
    // Reset error state and reload page
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
    window.location.reload();
  };

  handleGoHome = () => {
    // Reset error state and navigate home
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
    window.location.href = '/';
  };

  handleReset = () => {
    // Reset error state without reloading
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-container">
            <div className="error-boundary-icon">
              <AlertCircle size={64} />
            </div>
            
            <h1 className="error-boundary-title">Oops! Something went wrong</h1>
            
            <p className="error-boundary-message">
              We're sorry for the inconvenience. An unexpected error occurred.
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="error-boundary-details">
                  <summary>Error Details (Development Only)</summary>
                  <pre>{this.state.error.toString()}</pre>
                  {this.state.errorInfo && (
                    <pre>{this.state.errorInfo.componentStack}</pre>
                  )}
                </details>
              )}
            </p>

            {this.state.errorId && (
              <p className="error-boundary-id">
                Error ID: <code>{this.state.errorId}</code>
              </p>
            )}

            <div className="error-boundary-actions">
              <button
                onClick={this.handleReload}
                className="error-boundary-btn error-boundary-btn-primary"
              >
                <RefreshCw size={18} />
                Reload Page
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="error-boundary-btn error-boundary-btn-secondary"
              >
                <Home size={18} />
                Go Home
              </button>

              {process.env.NODE_ENV === 'development' && (
                <button
                  onClick={this.handleReset}
                  className="error-boundary-btn error-boundary-btn-tertiary"
                >
                  Try Again
                </button>
              )}
            </div>

            {process.env.NODE_ENV === 'development' && this.props.showDetails && (
              <div className="error-boundary-debug">
                <h3>Debug Information</h3>
                <pre>{JSON.stringify(this.state, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

